import classNames from 'classnames';
import React, { useContext, useMemo } from 'react';
import { Except, Extend } from '../type-utils';
import './SidebarView.scss';
import { useTeams } from '../TeamsProvider';

interface SidebarViewContextData {
  value: any
  onChange(value: any): void
}

const SidebarViewContext = React.createContext<SidebarViewContextData | null>(null)


interface SidebarViewProps<T> {
  value: T
  onChange(value: T): void
}

export function SidebarView<T>({ value, onChange, className, children, ...rest }: Extend<React.HTMLAttributes<HTMLDivElement>, SidebarViewProps<T>>) {
  const context = useMemo(() => ({
    value,
    onChange,
  }), [ onChange, value ])
  const { inTeams } = useTeams()

  return <SidebarViewContext.Provider value={context}>
    <div {...rest} className={classNames('SidebarView', inTeams && 'teams', className)}>
      {children}
    </div>
  </SidebarViewContext.Provider>
}

export const Sidebar = createSimpleFC('Sidebar', 'nav')
export const Body = createSimpleFC('SidebarViewBody', 'article')
export const Header = createSimpleFC('SidebarViewHeader', 'header')

function createSimpleFC<K extends keyof HTMLElementTagNameMap = 'div'>
  (componentClassName: string, tag: K): React.FC<React.HTMLAttributes<HTMLElementTagNameMap[K]>> {
  return function SimpleFC({ className, children, ...rest }: React.HTMLAttributes<HTMLElementTagNameMap[K]>) {
    return React.createElement(tag, {
      className: classNames(componentClassName, className),
      ...rest,
    }, children)
  }
}

interface ItemProps extends Except<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  value: any
}

export function Item({ value, className, children, ...rest }: ItemProps) {
  const context = useContext(SidebarViewContext)

  if (!context) {
    console.error('SidebarView.Item should be used inside SidebarView')
    return null
  }

  return <div {...rest} className={classNames('SidebarItem', className, value === context.value && 'active')} onClick={() => context.onChange(value)}>
    {children}
  </div>
}

export default Object.assign(SidebarView, {
  Sidebar,
  Item,
  Body,
  Header,
})
